import axios, {
  AxiosError,
  AxiosHeaders,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
} from 'axios';
import { showNotification } from '@mantine/notifications';
import { getDataFromLs, isChurchApiAllowedForScope } from '@/helpers';
import { authHeader } from '@/helpers/authHeader';
import { API_ACCEPT_LANGUAGE, API_TIMEOUT } from '@/constants/app';
import { ROUTE_ALIASES } from '@/routing/routesAliases';
import { impersonationService } from '@/services/impersonation.service';
import AuthService from '@/services/auth.service';
import { LsStoreKey, type ScopeListedItem } from '@/types';

const IMPERSONATION_HEADER = 'X-Impersonate-User';
const IMPERSONATION_REJECTED_NOTIFICATION_ID = 'impersonation-rejected';

let impersonationRequestActivityHandler: (() => void) | null = null;

export const setImpersonationRequestActivityHandler = (handler: (() => void) | null): void => {
  impersonationRequestActivityHandler = handler;
};

class ApiService {
  private api: AxiosInstance;
  constructor() {
    this.api = axios.create({
      baseURL: import.meta.env.VITE_API_URL || '/api',
      timeout: API_TIMEOUT,
      headers: {
        Accept: 'application/json, text/plain, */*',
        'Accept-Language': API_ACCEPT_LANGUAGE,
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    this.api.interceptors.request.use(
      async (config) => {
        const isRequestAllowed = this.isRequestAllowedForSelectedScope({ config });
        if (!isRequestAllowed) {
          const requestPathname = this.getRequestPathname({ config });
          return Promise.reject(
            new Error(`This request is not allowed for group scope. Path: ${requestPathname || 'unknown'}`)
          );
        }

        impersonationRequestActivityHandler?.();

        config.headers.set('Accept-Language', API_ACCEPT_LANGUAGE);

        const headers = await authHeader();

        const accessToken = headers.get('access-token');

        if (!accessToken) {
          return config;
        }

        Object.keys(headers).forEach((key) => {
          config.headers.set(key, headers.get(key));
        });

        const impersonatedUserEmail = impersonationService.getImpersonatedUserEmail();
        if (impersonatedUserEmail) {
          config.headers.set(IMPERSONATION_HEADER, impersonatedUserEmail);
        }

        config.headers.set('Accept-Language', API_ACCEPT_LANGUAGE);

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    this.api.interceptors.response.use(
      (response) => response,
      (error: unknown) => {
        const axiosError = error as AxiosError<{ message?: string }>;
        const status = axiosError.response?.status;
        const responseMessage = axiosError.response?.data?.message;
        const requestImpersonationHeader = this.getImpersonationHeaderFromRequest({
          requestHeaders: axiosError.config?.headers,
        });
        const isImpersonationRequest = Boolean(requestImpersonationHeader?.trim());
        const hasImpersonationErrorMessage =
          typeof responseMessage === 'string' && responseMessage.toLowerCase().includes('impersonat');
        const isImpersonationRejected =
          isImpersonationRequest &&
          (status === 403 || status === 400 || (status === 401 && hasImpersonationErrorMessage));

        if (isImpersonationRejected) {
          showNotification({
            id: IMPERSONATION_REJECTED_NOTIFICATION_ID,
            title: 'Impersonation rejected',
            message: 'The server rejected impersonation. Use "End impersonation" to reset the session.',
            color: 'red',
            autoClose: false,
            withCloseButton: true,
          });
        }

        // Any non-impersonation 401 means the session is no longer valid.
        // Do not match a specific backend message — wording varies
        // ("Unable to validate access token.", "Access token is not active", etc.).
        if (status === 401 && !isImpersonationRejected) {
          AuthService.clearTokens();
          if (window.location.pathname !== ROUTE_ALIASES.LOGIN) {
            window.location.href = ROUTE_ALIASES.LOGIN;
          }
        }

        return Promise.reject(error);
      }
    );
  }

  private isRequestAllowedForSelectedScope({ config }: { config: AxiosRequestConfig }): boolean {
    const selectedScope = getDataFromLs(LsStoreKey.LastSelectedChurch) as ScopeListedItem | undefined;
    const requestPathname = this.getRequestPathname({ config });

    return isChurchApiAllowedForScope({
      isGroupScope: selectedScope?.isGroupScope,
      isLimitedScope: selectedScope?.isLimitedScope,
      scopeTypes: selectedScope?.scopeTypes,
      requestPathname,
    });
  }

  private getRequestPathname({ config }: { config: AxiosRequestConfig }): string {
    if (!config.url) {
      return '';
    }

    const resolvedUrl = new URL(config.url, this.api.defaults.baseURL || window.location.origin);
    return resolvedUrl.pathname.replace(/^\/api\/v1/, '');
  }

  private getImpersonationHeaderFromRequest({
    requestHeaders,
  }: {
    requestHeaders: AxiosRequestConfig['headers'] | undefined;
  }): string | null {
    if (requestHeaders instanceof AxiosHeaders) {
      const header = requestHeaders.get(IMPERSONATION_HEADER);
      return typeof header === 'string' ? header : null;
    }

    if (!requestHeaders || Array.isArray(requestHeaders)) {
      return null;
    }

    const headerValue = requestHeaders[IMPERSONATION_HEADER];
    return typeof headerValue === 'string' ? headerValue : null;
  }

  /* eslint-disable @typescript-eslint/no-explicit-any */
  get<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.api.get(url, config);
  }

  post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.api.post(url, data, config);
  }

  put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.api.put(url, data, config);
  }

  delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.api.delete(url, config);
  }

  patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.api.patch(url, data, config);
  }
}

export default new ApiService();
